import { z } from "zod";
import { AspectRatioSchema } from "./common.ts";

export const TagSlugSchema = z
  .string()
  .regex(/^[a-z0-9-]+$/)
  .min(1)
  .max(40);

export const SubmissionImageInputSchema = z.object({
  r2AccountId: z.string().uuid(),
  r2Key: z.string().min(1).max(512).regex(/^submissions\//),
  altText: z.string().max(200).optional(),
});

/**
 * Self-edit image input: extends SubmissionImageInputSchema by ALSO accepting
 * `prompts/<uuid>/<n>.<ext>` keys — the user has chosen to keep an image
 * that's already attached to their existing prompt. The me-prompts edit
 * route additionally validates that any kept key actually belongs to the
 * prompt being edited (so you can't smuggle in a key from someone else's
 * prompt). New uploads still arrive via the regular submissions/ keyspace.
 */
export const SelfEditImageInputSchema = z.object({
  r2AccountId: z.string().uuid(),
  r2Key: z
    .string()
    .min(1)
    .max(512)
    .regex(/^(submissions|prompts)\//),
  altText: z.string().max(200).optional(),
});

export const SubmissionInputSchema = z
  .object({
    // Title is language-agnostic — a single string the contributor types in
    // whatever language they prefer. The API ingest writes it to BOTH
    // `prompts.title_zh` and `prompts.title_en` so the existing bilingual
    // fallback display (`pickBilingual`) doesn't need changes.
    title: z
      .string({
        required_error: "title_required",
        invalid_type_error: "title_required",
      })
      .trim()
      .min(1, { message: "title_required" })
      .max(200, { message: "title_too_long" }),
    promptZh: z.string().trim().max(8000, { message: "prompt_too_long" }).optional(),
    promptEn: z.string().trim().max(8000, { message: "prompt_too_long" }).optional(),
    negativePromptZh: z
      .string()
      .trim()
      .max(2000, { message: "negative_too_long" })
      .optional(),
    negativePromptEn: z
      .string()
      .trim()
      .max(2000, { message: "negative_too_long" })
      .optional(),
    notesZh: z.string().trim().max(2000, { message: "notes_too_long" }).optional(),
    notesEn: z.string().trim().max(2000, { message: "notes_too_long" }).optional(),
    aspectRatio: AspectRatioSchema.optional(),
    categoryId: z
      .string({
        required_error: "category_required",
        invalid_type_error: "category_required",
      })
      .uuid({ message: "category_required" }),
    tagSlugs: z
      .array(TagSlugSchema)
      .max(6, { message: "too_many_tags" })
      .default([]),
    images: z
      .array(SubmissionImageInputSchema)
      .min(1, { message: "images_required" })
      .max(5, { message: "too_many_images" }),
  })
  // Prompt is still bilingual but only ONE language is required. The
  // contributor can fill zh, en, or both — at least one non-empty.
  .refine((v) => Boolean(v.promptZh || v.promptEn), {
    message: "prompt_required",
    path: ["promptZh"],
  });

/**
 * Self-edit input: same shape as SubmissionInputSchema but the image array
 * uses SelfEditImageInputSchema so the user can KEEP any image already in
 * their prompt's `prompts/<id>/*` keyspace. The me-prompts edit route is
 * responsible for the cross-key validation (kept key → must belong to the
 * prompt being edited).
 */
export const SelfEditInputSchema = z
  .object({
    title: z
      .string({
        required_error: "title_required",
        invalid_type_error: "title_required",
      })
      .trim()
      .min(1, { message: "title_required" })
      .max(200, { message: "title_too_long" }),
    promptZh: z.string().trim().max(8000, { message: "prompt_too_long" }).optional(),
    promptEn: z.string().trim().max(8000, { message: "prompt_too_long" }).optional(),
    negativePromptZh: z
      .string()
      .trim()
      .max(2000, { message: "negative_too_long" })
      .optional(),
    negativePromptEn: z
      .string()
      .trim()
      .max(2000, { message: "negative_too_long" })
      .optional(),
    notesZh: z.string().trim().max(2000, { message: "notes_too_long" }).optional(),
    notesEn: z.string().trim().max(2000, { message: "notes_too_long" }).optional(),
    aspectRatio: AspectRatioSchema.optional(),
    categoryId: z
      .string({
        required_error: "category_required",
        invalid_type_error: "category_required",
      })
      .uuid({ message: "category_required" }),
    tagSlugs: z
      .array(TagSlugSchema)
      .max(6, { message: "too_many_tags" })
      .default([]),
    images: z
      .array(SelfEditImageInputSchema)
      .min(1, { message: "images_required" })
      .max(5, { message: "too_many_images" }),
  })
  .refine((v) => Boolean(v.promptZh || v.promptEn), {
    message: "prompt_required",
    path: ["promptZh"],
  });

export const PresignRequestSchema = z.object({
  filename: z.string().max(200),
  contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  size: z.number().int().positive().max(10 * 1024 * 1024),
});

export const PresignResponseSchema = z.object({
  r2AccountId: z.string().uuid(),
  r2Key: z.string(),
  uploadUrl: z.string().url(),
  expiresAt: z.string().datetime(),
});

export const RejectInputSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

export const ApproveInputSchema = z.object({
  edits: z
    .object({
      titleZh: z.string().max(200).optional(),
      titleEn: z.string().max(200).optional(),
      promptZh: z.string().max(8000).optional(),
      promptEn: z.string().max(8000).optional(),
      negativePromptZh: z.string().max(2000).optional(),
      negativePromptEn: z.string().max(2000).optional(),
      notesZh: z.string().max(2000).optional(),
      notesEn: z.string().max(2000).optional(),
      aspectRatio: AspectRatioSchema.optional(),
      categoryId: z.string().uuid().optional(),
      tagSlugs: z.array(TagSlugSchema).max(6).optional(),
    })
    .partial()
    .optional(),
});

export const CommunityGuidelinesAcceptSchema = z.object({
  version: z.number().int().min(1),
});

export const NotificationSchema = z.object({
  id: z.string().uuid(),
  type: z.enum([
    "submission_approved",
    "submission_rejected",
    "prompt_liked",
    "prompt_favorited",
  ]),
  payload: z.record(z.unknown()),
  readAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  aggregatedCount: z.number().int().min(1).default(1),
});

// Output DTOs

const PrimaryImageRefSchema = z
  .object({
    r2AccountId: z.string().uuid(),
    r2Key: z.string(),
  })
  .nullable();

export const SubmissionListItemSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["pending", "approved", "rejected"]),
  titleZh: z.string().nullable(),
  titleEn: z.string().nullable(),
  rejectReason: z.string().nullable(),
  primaryImage: PrimaryImageRefSchema,
  promotedTo: z
    .object({ promptId: z.string().uuid(), slug: z.string() })
    .nullable(),
  // When set, this submission is a user-self-edit of an existing prompt.
  // The UI surfaces a small "edit" badge alongside the row so users + admins
  // can tell pending edits apart from fresh submissions.
  originalPromptId: z.string().uuid().nullable(),
  originalPromptSlug: z.string().nullable(),
  // Whether this submission targets the nsfw category. Surfaced on the list
  // DTO so the mod-queue row can blur the thumbnail behind a click-to-reveal
  // overlay without needing the detail endpoint. The nsfw-tag invariant
  // (assertNsfwTagInvariant) guarantees this is equivalent to
  // tagSlugs === ["nsfw"], but we derive it from the category to keep the
  // signal authoritative at the source.
  isNsfw: z.boolean(),
  createdAt: z.string().datetime(),
  reviewedAt: z.string().datetime().nullable(),
});

export const AdminSubmissionListItemSchema = SubmissionListItemSchema.extend({
  contributor: z.object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    rejectedCount: z.number().int(),
  }),
});

export const AdminSubmissionDetailSchema = AdminSubmissionListItemSchema.extend({
  promptZh: z.string().nullable(),
  promptEn: z.string().nullable(),
  negativePromptZh: z.string().nullable(),
  negativePromptEn: z.string().nullable(),
  notesZh: z.string().nullable(),
  notesEn: z.string().nullable(),
  aspectRatio: AspectRatioSchema.nullable(),
  categoryId: z.string().uuid(),
  tagSlugs: z.array(z.string()),
  agreedGuidelinesVersion: z.number().int(),
  images: z.array(
    z.object({
      r2AccountId: z.string().uuid(),
      r2Key: z.string(),
      altText: z.string().nullable(),
    }),
  ),
});

export type SubmissionInput = z.infer<typeof SubmissionInputSchema>;
export type SelfEditInput = z.infer<typeof SelfEditInputSchema>;
export type PresignRequest = z.infer<typeof PresignRequestSchema>;
export type PresignResponse = z.infer<typeof PresignResponseSchema>;
export type RejectInput = z.infer<typeof RejectInputSchema>;
export type ApproveInput = z.infer<typeof ApproveInputSchema>;
export type CommunityGuidelinesAccept = z.infer<typeof CommunityGuidelinesAcceptSchema>;
export type NotificationDTO = z.infer<typeof NotificationSchema>;
export type SubmissionListItem = z.infer<typeof SubmissionListItemSchema>;
export type AdminSubmissionListItem = z.infer<typeof AdminSubmissionListItemSchema>;
export type AdminSubmissionDetail = z.infer<typeof AdminSubmissionDetailSchema>;
// Note: AspectRatio/AspectRatioSchema is re-used from ./common.ts — no new export.
